import React, { useState, useEffect } from "react";

const NumberInput = ({
  value,
  onChange,
  onBlur,
  placeholder = "0",
  step = "1",
  min,
  max,
  disabled = false,
  className = "",
  prefix = "",
  suffix = "",
  defaultToZero = true,
  showValidationMessage = true,
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");
  const [isFocused, setIsFocused] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  useEffect(() => {
    if (isFocused) {
      return;
    }

    if (value !== undefined && value !== null) {
      setLocalValue(value.toString());
    } else {
      setLocalValue("");
    }
  }, [value, isFocused]);

  useEffect(() => {
    if (validationMessage) {
      const timer = setTimeout(() => setValidationMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [validationMessage]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    setValidationMessage("");

    if (onChange) {
      if (newValue === "" || newValue === "-") {
        onChange(newValue);
      } else {
        const parsedValue = parseFloat(newValue);
        onChange(isNaN(parsedValue) ? "" : parsedValue);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setValidationMessage("");
  };

  const handleBlur = (e) => {
    setIsFocused(false);

    let finalValue = e.target.value;

    if (finalValue !== "" && finalValue !== "-") {
      let numericValue = parseFloat(finalValue);

      if (!isNaN(numericValue)) {
        if (min !== undefined && numericValue < parseFloat(min)) {
          numericValue = parseFloat(min);
          if (showValidationMessage) {
            setValidationMessage(`Minimum value is ${min}`);
          }
        } else if (max !== undefined && numericValue > parseFloat(max)) {
          numericValue = parseFloat(max);
          if (showValidationMessage) {
            setValidationMessage(`Maximum value is ${max}`);
          }
        }

        finalValue = numericValue.toString();
        setLocalValue(finalValue);

        if (onChange) {
          onChange(numericValue);
        }
      }
    } else if (defaultToZero) {
      let defaultValue = 0;
      if (min !== undefined && defaultValue < parseFloat(min)) {
        defaultValue = parseFloat(min);
      }
      finalValue = defaultValue.toString();
      setLocalValue(finalValue);
      if (onChange) {
        onChange(defaultValue);
      }
    } else {
      setLocalValue("");
      if (onChange) {
        onChange("");
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
          {prefix}
        </span>
      )}
      <input
        type="number"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        step={step}
        min={min}
        max={max}
        placeholder={placeholder}
        disabled={disabled}
        className={`
          ${prefix ? "pl-8" : "px-3"} 
          ${suffix ? "pr-8" : "px-3"} 
          py-2 w-full border rounded-lg
          ${validationMessage ? "border-amber-500" : "border-gray-300"}
          disabled:bg-gray-100 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-blue-500
          [&::-webkit-inner-spin-button]:appearance-none
          [&::-webkit-outer-spin-button]:appearance-none
          [-moz-appearance:textfield]
          ${className}
        `}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
          {suffix}
        </span>
      )}
      {validationMessage && (
        <p className="mt-1 text-xs text-amber-600 font-medium">
          {validationMessage}
        </p>
      )}
    </div>
  );
};

export default NumberInput;
